import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Gift, ArrowRight, CalendarDays } from "lucide-react";
import { useLenis, scrollToSection } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FreeTip = () => {
    const lenisRef = useLenis();
    const [tip, setTip] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/content`)
            .then((res) => setTip(res.data?.free_tip || null))
            .catch(() => {});
    }, []);

    const ready = tip && tip.teams;

    return (
        <section id="free-tip" data-testid="free-tip-section" className="relative py-20 lg:py-28 overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-bn-blue/10 blur-[110px] rounded-full" />
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-10"
                >
                    <div className="font-mono text-[11px] tracking-[0.3em] text-bn-blue font-semibold mb-4 flex items-center justify-center gap-2">
                        <Gift className="w-3.5 h-3.5" /> FREE DAILY TIP
                    </div>
                    <h2 data-testid="free-tip-heading" className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white">
                        Today's free pick, <span className="text-bn-blue text-glow-blue">on us</span>
                    </h2>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.97 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="relative rounded-2xl border border-bn-blue/30 bg-gradient-to-br from-bn-blue/10 via-[#0B0F17] to-bn-cyan/5 p-8 sm:p-12 text-center shadow-[0_30px_80px_rgba(0,102,255,0.12)]"
                    data-testid="free-tip-card"
                >
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-bn-green/90 text-[#05070A] font-mono text-[10px] font-bold tracking-[0.2em] shadow-[0_0_20px_rgba(16,185,129,0.4)]">
                        100% FREE
                    </span>

                    {ready ? (
                        <>
                            <div className="flex items-center justify-center gap-2 font-mono text-xs text-slate-500 tracking-wider">
                                <CalendarDays className="w-3.5 h-3.5" />
                                <span data-testid="free-tip-date">{tip.date}</span>
                            </div>
                            <h3 data-testid="free-tip-teams" className="mt-4 font-display font-extrabold tracking-tight text-2xl sm:text-3xl text-white">
                                {tip.teams}
                            </h3>
                            <div className="mt-5 inline-flex items-center gap-3">
                                <span data-testid="free-tip-pick" className="font-mono text-sm font-bold px-4 py-2 rounded-lg bg-bn-blue/15 text-bn-cyan border border-bn-blue/40">
                                    {tip.pick}
                                </span>
                                {tip.odds && <span className="font-display font-extrabold text-2xl text-bn-blue">@{tip.odds}</span>}
                            </div>
                            {tip.note && (
                                <p data-testid="free-tip-note" className="mt-5 text-sm text-slate-400 leading-relaxed max-w-md mx-auto">
                                    {tip.note}
                                </p>
                            )}
                        </>
                    ) : (
                        <p data-testid="free-tip-pending" className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto">
                            Today's free tip is being prepared by our analysts — check back soon.
                        </p>
                    )}

                    <div className="mt-8 pt-6 border-t border-white/5">
                        <p className="text-xs text-slate-500 mb-4">Want the full Singles &amp; Doubles every day?</p>
                        <button
                            data-testid="free-tip-cta-button"
                            onClick={() => scrollToSection(lenisRef.current, "subscription")}
                            className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,102,255,0.55)]"
                        >
                            VIEW SUBSCRIPTION PLANS
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
